/** "Containers": each box one of your cars is in, drawn as a box. Other customers' cars are
 *  filled slots and a count, never a name or a VIN. */
import React from "react";
import { useMyContainers } from "@/services/python-api/customerHooks";
import { Card, Empty, Loading, Page, Pill, day } from "./parts";

const BLUE = "#2563EB";

/** A 2D cut-away: the box from the side, one slot per car. */
function CutAway({ box }) {
  const slots = box.slots;
  const yours = box.your_cars;
  const othersIn = box.other_cars.in;
  const othersOut = box.other_cars.count - othersIn;
  const cells = [
    ...yours.map((c) => ({ kind: "yours", car: c })),
    ...Array.from({ length: othersIn }, () => ({ kind: "other" })),
    ...Array.from({ length: othersOut }, () => ({ kind: "other-coming" })),
  ];
  while (cells.length < slots) cells.push({ kind: "empty" });
  const W = 640, H = 170, pad = 14, gap = 8;
  const slotW = (W - pad * 2 - gap * (slots - 1)) / slots;
  return (
    <svg viewBox={`0 0 ${W} ${H + 26}`} className="w-full" role="img" aria-label={`Container with ${slots} slots`}>
      <defs>
        <pattern id="ribs" width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill="#F2F4F7" />
          <rect width="2" height="10" fill="#E4E7EC" />
        </pattern>
      </defs>
      <rect x="2" y="2" width={W - 4} height={H - 4} rx="10" fill="url(#ribs)" stroke="#98A2B3" strokeWidth="2" />
      <rect x={W - 10} y="18" width="6" height={H - 36} rx="2" fill="#D0D5DD" />
      {cells.map((cell, i) => {
        const x = pad + i * (slotW + gap);
        const y = 30;
        const h = H - 60;
        const fill =
          cell.kind === "yours"
            ? cell.car.in
              ? BLUE
              : "#DBEAFE"
            : cell.kind === "other"
            ? "#98A2B3"
            : cell.kind === "other-coming"
            ? "#E4E7EC"
            : "white";
        const dashed = cell.kind === "empty" || cell.kind === "other-coming" || (cell.kind === "yours" && !cell.car.in);
        const text = cell.kind === "yours" && cell.car.in ? "white" : "#344054";
        return (
          <g key={i}>
            <rect x={x} y={y} width={slotW} height={h} rx="8" fill={fill} stroke={cell.kind === "yours" ? BLUE : "#98A2B3"}
              strokeDasharray={dashed ? "5 4" : undefined} strokeWidth="1.5" />
            {/* A car's silhouette: a low body and two wheels. */}
            {cell.kind !== "empty" ? (
              <g opacity={cell.kind === "yours" ? 1 : 0.8}>
                <rect x={x + slotW * 0.14} y={y + h * 0.42} width={slotW * 0.72} height={h * 0.2} rx="6"
                  fill={cell.kind === "yours" && cell.car.in ? "#1D4ED8" : "#667185"} opacity="0.35" />
                <circle cx={x + slotW * 0.3} cy={y + h * 0.66} r={Math.min(9, slotW * 0.06)} fill="#344054" opacity="0.5" />
                <circle cx={x + slotW * 0.7} cy={y + h * 0.66} r={Math.min(9, slotW * 0.06)} fill="#344054" opacity="0.5" />
              </g>
            ) : null}
            <text x={x + slotW / 2} y={y + 22} textAnchor="middle" fontSize="13" fontWeight="600" fill={text}>
              {cell.kind === "yours" ? `…${cell.car.last6}` : cell.kind === "empty" ? "Free" : "Another car"}
            </text>
            <text x={x + slotW / 2} y={y + h - 12} textAnchor="middle" fontSize="11" fill={text}>
              {cell.kind === "yours" ? (cell.car.in ? "In the box" : "On its way") : cell.kind === "other-coming" ? "On its way" : ""}
            </text>
          </g>
        );
      })}
      <text x="4" y={H + 20} fontSize="12" fill="#667185">
        {box.size || "Container"} · {box.container || box.booking}
      </text>
    </svg>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[12px] text-[#667185]">{label}</p>
      <p className="text-[14px] font-medium text-[#101928]">{value}</p>
    </div>
  );
}

export default function MyContainers() {
  const { data, isLoading } = useMyContainers();
  const boxes = data?.containers ?? [];
  return (
    <Page title="Containers">
      {isLoading ? (
        <Loading />
      ) : boxes.length === 0 ? (
        <Empty title="No containers yet">When your cars are booked into a container, you'll see it here.</Empty>
      ) : (
        <div className="space-y-4">
          {boxes.map((box) => (
            <Card key={box.container || box.booking}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[16px] font-semibold text-[#101928]">{box.container || "Container to be assigned"}</p>
                  <p className="text-[13px] text-[#667185]">
                    {[box.line, box.vessel && `${box.vessel}${box.voyage ? ` ${box.voyage}` : ""}`].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <Pill tone={box.loaded ? "bg-[#E7F6EC] text-[#036B26]" : "bg-[#EFF6FF] text-[#1D4ED8]"}>
                  {box.loaded ? "Loaded" : `${box.filled} of ${box.slots} in`}
                </Pill>
              </div>
              <div className="mt-4">
                <CutAway box={box} />
              </div>
              <p className="mt-3 text-[15px] font-medium text-[#101928]">{box.status}</p>
              {box.wait ? (
                <p className="mt-1.5 rounded-xl bg-[#EFF6FF] px-3 py-2 text-[14px] text-[#1D4ED8]">
                  {box.wait[0].toUpperCase() + box.wait.slice(1)}
                </p>
              ) : null}
              {box.waiting_on?.length && !box.loaded ? (
                <p className="mt-1.5 text-[13px] text-[#475367]">Still to come: {box.waiting_on.join(", ")}.</p>
              ) : null}
              <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-[#F9FAFB] p-3 sm:grid-cols-4">
                <Fact label="Booking" value={box.booking} />
                <Fact label="Cut-off" value={day(box.cut_off)} />
                <Fact label="Sails" value={day(box.sails)} />
                <Fact label="Arrives" value={day(box.eta)} />
                <Fact label="From" value={box.from} />
                <Fact label="To" value={box.to} />
              </div>
              <div className="mt-4">
                <p className="text-[13px] font-medium text-[#667185]">Your cars in this box</p>
                <ul className="mt-1 divide-y divide-[#F0F2F5]">
                  {box.your_cars.map((c) => (
                    <li key={c.last6} className="flex items-center justify-between py-2 text-[14px]">
                      <span className="text-[#101928]">
                        {c.car} <span className="text-[#667185]">…{c.last6}</span>
                      </span>
                      <span className={c.in ? "text-[#036B26]" : "text-[#865503]"}>{c.in ? "In the box" : "On its way"}</span>
                    </li>
                  ))}
                </ul>
                {box.shared ? (
                  <p className="mt-2 text-[13px] text-[#667185]">
                    Shared with {box.other_cars.count} other {box.other_cars.count === 1 ? "car" : "cars"}.
                  </p>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
